import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class NetworkOptimizationService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  /**
   * Preload critical resources for faster loading
   */
  preloadCriticalResources(): void {
    if (!this.isBrowser) return;

    // Preload critical fonts
    this.preloadResource('/assets/fonts/bwgradualdemo-regular-webfont.woff2', 'font');
    
    // Preload critical images
    this.preloadResource('/assets/banner-preload-image.webp', 'image');
    
    // Preload critical API endpoints
    this.preloadResource('https://api.satel.sa/categories/all', 'fetch');
  }

  /**
   * Preload resource with specific type
   */
  private preloadResource(url: string, type: 'image' | 'font' | 'script' | 'style' | 'fetch'): void {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    
    switch (type) {
      case 'image':
        link.as = 'image';
        break;
      case 'font':
        link.as = 'font';
        link.crossOrigin = 'anonymous';
        break;
      case 'script':
        link.as = 'script';
        break;
      case 'style':
        link.as = 'style';
        break;
      case 'fetch':
        link.as = 'fetch';
        link.crossOrigin = 'anonymous';
        break;
    }
    
    document.head.appendChild(link);
  }

  /**
   * Optimize critical path by deferring non-critical resources
   */
  deferNonCriticalResources(): void {
    if (!this.isBrowser) return;

    // Defer analytics scripts
    this.deferScript('https://www.googletagmanager.com/gtag/js?id=G-VJ28XRQ95E');
    this.deferScript('https://www.googletagmanager.com/gtag/js?id=AW-11491373825');
    
    // Note: Google Maps is NOT deferred as it's needed for map functionality
    // this.deferScript('https://maps.googleapis.com/maps/api/js');
  }

  /**
   * Defer script loading
   */
  private deferScript(src: string): void {
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.async = true;
    document.head.appendChild(script);
  }

  /**
   * Optimize bundle loading with intersection observer
   */
  setupLazyBundleLoading(): void {
    if (!this.isBrowser) return;

    const bundleObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const bundleUrl = element.dataset['bundle'];
          
          if (bundleUrl) {
            this.loadBundle(bundleUrl);
            bundleObserver.unobserve(element);
          }
        }
      });
    }, {
      rootMargin: '100px 0px',
      threshold: 0.1
    });

    // Observe elements that need bundles
    document.querySelectorAll('[data-bundle]').forEach(element => {
      bundleObserver.observe(element);
    });
  }

  /**
   * Load bundle dynamically
   */
  private loadBundle(bundleUrl: string): void {
    const script = document.createElement('script');
    script.src = bundleUrl;
    script.async = true;
    document.head.appendChild(script);
  }

  /**
   * Optimize API calls with connection pooling
   */
  optimizeApiCalls(): void {
    if (!this.isBrowser) return;

    // Set up connection pooling for API calls
    const apiBaseUrl = 'https://api.satel.sa';
    
    // Pre-warm connection
    fetch(`${apiBaseUrl}/health`, { 
      method: 'HEAD',
      mode: 'cors'
    }).catch(() => {
      // Ignore errors for pre-warming
    });
  }

  /**
   * Get network optimization strategy based on connection
   */
  getOptimizationStrategy(): 'aggressive' | 'moderate' | 'conservative' {
    if (!this.isBrowser) return 'moderate';
    
    if (!('connection' in navigator)) return 'moderate';
    
    const connection = (navigator as any).connection;
    
    if (connection.effectiveType === '4g' && connection.downlink > 10) {
      return 'aggressive';
    } else if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      return 'conservative';
    }
    
    return 'moderate';
  }

  /**
   * Apply optimization strategy
   */
  applyOptimizationStrategy(): void {
    const strategy = this.getOptimizationStrategy();
    
    switch (strategy) {
      case 'aggressive':
        this.preloadCriticalResources();
        this.optimizeApiCalls();
        break;
      case 'moderate':
        this.preloadCriticalResources();
        break;
      case 'conservative':
        // Only preload essential resources
        this.preloadResource('/assets/banner-preload-image.webp', 'image');
        break;
    }
  }
} 